/**
 * Labstep
 *
 * @module state/reducers/entity
 * @desc Reducers for any entity
 */

import type { EntityName } from 'labstep-web/services/schema/schema';
import fileReducers from 'labstep-web/state/reducers/file';
import userReducers from 'labstep-web/state/reducers/user';
import { combineReducers } from 'redux';
import defaultById from './byId';
import creatingIds from './creatingIds';
import deletingIds from './deletingIds';
import readingIds from './readingIds';
import togglingIds from './togglingIds';
import updatingIds from './updatingIds';

/**
 * 3 main parts:
 * - Denormalized entities: byId
 * - CRUD: creatingIds, readingIds, updatingIds, deletingIds
 * - Add/Remove parent/child: togglingIds
 *
 * @function
 * @param  {string} entityName - Entity name
 * @param  {object} reducers - Additional sub-reducers to customise final result
 */
export const getEntityReducers = (
  entityName: string,
  reducers?: any,
): any => {
  const entityNameUppercase = entityName.toUpperCase();

  let extraReducers = {};
  if (entityName === 'file') {
    extraReducers = fileReducers;
  }
  if (entityName === 'user') {
    extraReducers = userReducers;
  }

  /**
   * Returns reducer for entities denormalized by id
   *
   * @function
   * @param  {object} state - Redux state
   * @param  {string} action - Redux action
   */
  const byId = (state: any = {}, action: any): any =>
    defaultById(entityName as EntityName, entityNameUppercase)(
      state,
      action,
    );

  return combineReducers({
    byId,
    creatingIds: creatingIds(entityName, entityNameUppercase),
    readingIds: readingIds(entityName, entityNameUppercase),
    updatingIds: updatingIds(entityName, entityNameUppercase),
    deletingIds: deletingIds(entityName, entityNameUppercase),
    togglingIds: togglingIds(entityName),
    ...reducers,
    ...extraReducers,
  });
};
